// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/ChatLinkify.tsx
// 🔧 PURPOSE: I link dentro i messaggi della chat, resi toccabili.
//
//             Lo staff incolla spesso la posizione di Google Maps o il sito
//             di un ristorante: come testo semplice l'ospite doveva copiarlo
//             a mano (e sul telefono il tocco lungo e' spento). Qui il testo
//             diventa nodi React, mai HTML iniettato: un messaggio non puo'
//             portare tag o script, al massimo un link http/https.
// ============================================

import React, { useMemo } from 'react';
import { ExternalLink, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// L'ultimo carattere esclude la punteggiatura: "vedi https://x.it/a." non
// deve portarsi dietro il punto finale, ne' la parentesi di "(www.x.it)".
const URL_PATTERN = /(https?:\/\/[^\s<]+[^\s<.,:;"')\]!?])|(www\.[^\s<]+[^\s<.,:;"')\]!?])/gi;

// Oltre questa lunghezza il percorso si accorcia: al chip serve dire "dove
// porta", non ripetere l'indirizzo intero (che resta nel title).
const MAX_PATH_CHARS = 18;

export interface LinkPart {
  href: string;
  host: string;
  path: string;
  isMaps: boolean;
}

/** I link di Google Maps (brevi e lunghi): diventano "Apri in Maps". */
export function isMapsUrl(u: URL): boolean {
  const host = u.hostname.toLowerCase();
  const path = u.pathname.toLowerCase();
  if (host === 'maps.app.goo.gl') return true;
  if (host === 'goo.gl' && path.indexOf('/maps') === 0) return true;
  if (/^maps\.google\.[a-z.]+$/.test(host)) return true;
  if (/^(www\.)?google\.[a-z.]+$/.test(host) && path.indexOf('/maps') === 0) return true;
  return false;
}

function toLinkPart(raw: string): LinkPart | null {
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let u: URL;
  try {
    u = new URL(candidate);
  } catch {
    return null;
  }
  // Solo web: niente javascript:, data:, intent: anche se un giorno la
  // regex si allargasse.
  if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
  if (!u.hostname) return null;
  const fullPath = (u.pathname === '/' ? '' : u.pathname) + u.search;
  const path = fullPath.length > MAX_PATH_CHARS ? `${fullPath.slice(0, MAX_PATH_CHARS)}…` : fullPath;
  return {
    href: u.href,
    host: u.hostname.replace(/^www\./i, ''),
    path,
    isMaps: isMapsUrl(u),
  };
}

/** Divide il testo in pezzi di testo semplice e link riconosciuti. */
export function splitLinks(text: string): Array<string | LinkPart> {
  const out: Array<string | LinkPart> = [];
  if (!text) return out;
  // Una regex nuova per ogni chiamata: quella globale conserva lastIndex.
  const re = new RegExp(URL_PATTERN.source, 'gi');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const link = toLinkPart(m[0]);
    // Un indirizzo che il browser non sa leggere resta testo: finira' nel
    // pezzo successivo.
    if (!link) continue;
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(link);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

interface ChatLinkifyProps {
  text: string;
  /** 'guest' = bolla blu dell'ospite, 'staff' = bolla chiara dello staff. */
  tone?: 'guest' | 'staff';
}

const ChatLinkify: React.FC<ChatLinkifyProps> = ({ text, tone = 'staff' }) => {
  const { t } = useTranslation();
  const parts = useMemo(() => splitLinks(text), [text]);

  // min-w-0 + max-w-full + truncate: un link lunghissimo resta dentro la
  // bolla e si accorcia con i puntini invece di allargarla oltre lo schermo.
  const chipBase =
    'inline-flex max-w-full min-w-0 items-center gap-1 align-middle rounded-full border px-2 py-0.5 my-0.5 text-[13px] font-medium leading-5 no-underline transition-colors';
  const mapsTone =
    tone === 'guest'
      ? 'bg-white text-blue-700 border-white hover:bg-blue-50'
      : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700';
  const linkTone =
    tone === 'guest'
      ? 'bg-white/15 text-white border-white/40 hover:bg-white/25'
      : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-300 dark:border-blue-800 dark:hover:bg-gray-700';

  return (
    <>
      {parts.map((part, i) => {
        if (typeof part === 'string') return <React.Fragment key={i}>{part}</React.Fragment>;
        if (part.isMaps) {
          return (
            <a
              key={i}
              href={part.href}
              target="_blank"
              rel="noopener noreferrer"
              title={part.href}
              className={`${chipBase} ${mapsTone}`}
            >
              <MapPin className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
              <span className="truncate min-w-0">{t('guestChat.openInMaps')}</span>
            </a>
          );
        }
        return (
          <a
            key={i}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            title={part.href}
            className={`${chipBase} ${linkTone}`}
          >
            <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            <span className="truncate min-w-0">
              {part.host}
              {part.path && <span className="opacity-70 font-normal">{part.path}</span>}
            </span>
          </a>
        );
      })}
    </>
  );
};

export default ChatLinkify;
